import { DictType } from "@pma/types";
import { useEffect, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/common/popover";
import { cn } from "@pma/utils/helpers";
import { Button } from "@components/common/button";
import axios from "axios";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useAuth } from "@stores/auth-store";
import { useMutation, useQuery } from "@tanstack/react-query";

import {
  EDIT_USER_ORG_PARAMETER_ENDPOINT,
  JOB_TITLE_DICTIONARY_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes";
import useTranslator from "@hooks/use-translator";
import { Separator } from "@components/common/separator";
import { toast } from "@hooks/use-toast";
import AddJobTitle from "./add-job-title";

interface Props {
  title?: string;
  refetch: CallableFunction;
  userId: string;
}
const JobTitleSelector = ({ title, userId, refetch }: Props) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(title);

  const { authConfig } = useAuth();
  const { translate, locale } = useTranslator();

  const isHebrew = locale === "he";

  const accessToken = parseCookies()[ACCESS_TOKEN];
  const orgId = authConfig?.last_organization_id;

  const getJobTitles = async () => {
    const response = await axios.get(
      SERVER_URL +
        `${JOB_TITLE_DICTIONARY_ENDPOINT}?context_type=organization%20user&context_id=${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const { data } = useQuery({
    queryKey: ["job-titles", orgId],
    queryFn: getJobTitles,
  });

  const updateUserDetails = async (dep: DictType) => {
    const response = await axios.post(
      SERVER_URL + EDIT_USER_ORG_PARAMETER_ENDPOINT,

      {
        user_id: userId,
        id_job_title: dep.id,
        organization_id: orgId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const updateUserJobTitleMutation = useMutation({
    mutationKey: [`job-title`],
    mutationFn: updateUserDetails,
    onSuccess: (data) => {
      setOpen(false);

      refetch && refetch();
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data.message ||
                translate(`Error occurred while updating`) +
                  " " +
                  translate(`department`),
        variant: "error",
      });
    },
  });

  useEffect(() => {
    title && setValue(title);
  }, [title]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className="w-full h-8 text-left flex items-center justify-start p-1 text-muted-foreground"
        >
          {title ? title : translate("Select Title")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0">
        <Command>
          <CommandInput placeholder={translate("Search for title") + "..."} />
          <CommandList className={cn(isHebrew ? "rtl" : "ltr")}>
            <CommandEmpty>{translate("No job titles found")}.</CommandEmpty>
            <CommandGroup>
              {data?.map((title: DictType) => (
                <CommandItem
                  key={title.id}
                  value={title?.name}
                  onSelect={(currentValue: any) => {
                    setValue(title?.name);
                    updateUserJobTitleMutation.mutate(title);
                  }}
                  className={cn(
                    "flex flex-col text-left items-start my-1 text-black",
                  )}
                >
                  {title.name}
                  <Separator className="mt-1" />
                </CommandItem>
              ))}
              <CommandItem>
                {/* Add job title */}
                <AddJobTitle />
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default JobTitleSelector;
